import {
  NITROFERT_BRAND_ROUTE,
  NITROFERT_DEFAULT_TITLE,
  NITROFERT_LOGO_ROUTE,
  RELEASE_NITROFERT_PORTAL_LOGIN_URL,
  SEXTANTE_BRAND_ROUTE,
  SEXTANTE_DEFAULT_TITLE,
  SEXTANTE_LOGO_ROUTE,
  TESTING_NITROFERT_PORTAL_LOGIN_URL,
} from "../constantes";
import { BaseAsideMenuComponent } from "../shared/components/index/app/BaseAsideMenuComponent";
import { Init, InitData } from "../shared/Init";
import { LoginPlataformaResponse } from "../shared/response/seguridad/LoginPlataformaResponse";
import { loginPlataformaGrpcService } from "../shared/services/seguridad/LoginPlataformaGrpcService";
import { AsideMenuComponent } from "./components/index/AsideMenuComponent";
import { ClientesPage } from "./pages/terceros/ClientesPage";
import { OrtomosaicoWatchService } from "./services/terceros/OrtomosaicoWatchService";
import { CostosPage } from "./pages/costos/CostosPage";
import { CultivosPage } from "./pages/catalogos/CultivosPage";
import { ProductosPage } from "./pages/catalogos/ProductosPage";
import { PromotoresPage } from "./pages/terceros/PromotoresPage";
import { UsuariosPage } from "./pages/usuarios/UsuariosPage";
import { PipelinePage } from "./pages/pipeline/PipelinePage";
import { HardwarePage } from "./pages/hardware/HardwarePage";
import { EstadosFenologicosPage } from "./pages/monitoreo/EstadosFenologicosPage";
import { PlagasCatalogoPage } from "./pages/monitoreo/PlagasCatalogoPage";
import { CampanasPage } from "./pages/marketing/CampanasPage";
import { ProyectosDashboardPage } from "./pages/proyectos/ProyectosDashboardPage";
import { ParametrosPage } from "./pages/proyectos/ParametrosPage";
import { TecnicosPage } from "./pages/proyectos/TecnicosPage";
import { CatalogoPage } from "./pages/proyectos/CatalogoPage";
import { ProyectosPage } from "./pages/proyectos/ProyectosPage";
import { MapaFincasPage } from "./pages/terceros/MapaFincasPage";
import { calcularMenu, rutaPermitida, tieneOpciones } from "./permisosMenu";
import { ProductosMarca } from "./productosMarca";
import { RUTA_SIN_PERMISOS, SinPermisosPage } from "./pages/SinPermisosPage";
import { NucleosPage } from "./pages/proyectos/NucleosPage";
import { MacroProyectosPage } from "./pages/proyectos/MacroProyectosPage";
import { CultivosPage as ProyectosCultivosPage } from "./pages/proyectos/CultivosPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";

/**
 * Arranca la Plataforma de una marca: marca el `<body>` con la marca, arma los datos de `Init` (ingreso con contraseña o
 * por el portal de Nitria, menú según el rol, la página de cada ruta y lo que pasa después del ingreso: el Dashboard,
 * «Mis proyectos» para un técnico o el aviso sin permisos) y lo inicia.
 * @param isSextante `true` para Sextante; `false` para Nitrofert.
 */
export function fx_plataformaInit(isSextante: boolean): void {
  // Marca en <body data-brand="…"> para poder escribir CSS exclusivo de una marca
  // (ver el bloque [data-brand="nitrofert"] en mainLayout.css) sin tocar la otra.
  document.body.dataset.brand = isSextante ? "sextante" : "nitrofert";
  // Lo que vende la marca (2026-10-20): Precios, Pipeline y Promotores muestran y cotizan solo eso.
  const productos = new ProductosMarca(isSextante);
  const data: InitData<LoginPlataformaResponse> = {
    login: (email, password) => loginPlataformaGrpcService.loginAsync(email, password),
    checkToken: (token) => loginPlataformaGrpcService.checkTokenAsync(token),
    //
    logoRoute: isSextante ? SEXTANTE_LOGO_ROUTE : NITROFERT_LOGO_ROUTE,
    brandRoute: isSextante ? SEXTANTE_BRAND_ROUTE : NITROFERT_BRAND_ROUTE,
    defaultTitle: isSextante ? SEXTANTE_DEFAULT_TITLE : NITROFERT_DEFAULT_TITLE,
    // Nitrofert entra solo por el portal de Nitria (SSO, 2026-09-30); Sextante sigue con su login.
    // El portal depende del ambiente (2026-10-05): el de producción solo en el build de prod.
    portal: isSextante
      ? undefined
      : {
          loginUrl: __IS_RELEASE__ ? RELEASE_NITROFERT_PORTAL_LOGIN_URL : TESTING_NITROFERT_PORTAL_LOGIN_URL,
          login: (token) => loginPlataformaGrpcService.loginPortalAsync(token),
        },
    //
    getAsideMenuComponentHandler: (
      response: LoginPlataformaResponse,
    ): BaseAsideMenuComponent => {
      return new AsideMenuComponent(response, isSextante);
    },
    onAfterLogin: (response: LoginPlataformaResponse) => {
      // Sin ninguna opción de menú (usuario creado desde el portal de Nitria, sin roles): aviso en vez de
      // la pantalla en blanco.
      if (!tieneOpciones(calcularMenu(response, isSextante))) {
        document.dispatchEvent(new CustomEvent("app:navigate", { detail: RUTA_SIN_PERMISOS }));
        return;
      }
      // Técnico agrónomo: portal restringido — no sincroniza módulos admin, aterriza en "Mis proyectos".
      if (response.tecnicoId != null) {
        document.dispatchEvent(new CustomEvent("app:navigate", { detail: "/proyectos/mis-proyectos" }));
        return;
      }
      // Sin precargas: cada página pide lo suyo al abrirse.
      // Consulta las publicaciones de ortomosaico en curso y se apaga sola si no hay
      // ninguna. Cubre las encoladas desde otra sesión o por otro usuario. Solo para
      // quien ve Clientes: el Server rechaza la consulta a los demás roles.
      const menu = calcularMenu(response, isSextante);
      if (menu.showClientes) OrtomosaicoWatchService.instance.start();
      // Abre en el Dashboard (2026-10-28), en vez de la pantalla vacía. Quien no ve ninguno de sus recuadros
      // (por ejemplo, solo Flota) sigue entrando sin página abierta.
      if (menu.showDashboard) document.dispatchEvent(new CustomEvent("app:navigate", { detail: "/dashboard" }));
    },
    getPagesHandler: (response: LoginPlataformaResponse) => (appRoute: string) => {
      // Una ruta escrita a mano en la URL no salta el menú: si el rol no la ve, no se abre
      // (el Server igual rechazaría sus funciones).
      const menu = calcularMenu(response, isSextante);
      if (!tieneOpciones(menu)) return new SinPermisosPage();
      if (!rutaPermitida(appRoute, menu)) return null;
      // Si el usuario es promotor, sus vistas comerciales se acotan a su cartera.
      // Un admin queda SIEMPRE sin acotar, aunque además tenga ficha de promotor:
      // si no, un superusuario con ficha vería solo sus propias oportunidades.
      const esAdmin = response.hasSuperAdmin;
      const promotorId = esAdmin ? null : response.promotorId;
      // Comercial sin ficha de promotor: no hay `comercialId` por el que filtrar (la FK de la
      // oportunidad apunta a tbl_promotores), así que la vista no se acota y ve todo el pipeline.
      const comercialSinPromotor = !esAdmin && response.hasComercial && response.promotorId == null;
      if (appRoute === "/dashboard")                 return new DashboardPage(menu, promotorId, isSextante);
      if (appRoute === "/terceros/clientes")         return new ClientesPage(promotorId, menu.showUsuariosApp);
      if (appRoute === "/terceros/mapa-fincas")      return new MapaFincasPage(promotorId);
      if (appRoute === "/terceros/promotores")       return new PromotoresPage(productos);
      if (appRoute === "/admin/costos")              return new CostosPage(productos);
      if (appRoute === "/admin/catalogos/productos") return new ProductosPage();
      if (appRoute === "/admin/catalogos/cultivos")  return new CultivosPage();
      if (appRoute === "/admin/usuarios")            return new UsuariosPage(!isSextante);
      if (appRoute === "/ventas/pipeline")           return new PipelinePage(productos, promotorId, comercialSinPromotor);
      if (appRoute === "/sistema/hardware")          return isSextante ? new HardwarePage() : null;
      if (appRoute === "/monitoreo/estados-fenologicos") return new EstadosFenologicosPage();
      if (appRoute === "/monitoreo/plagas")          return new PlagasCatalogoPage();
      if (appRoute === "/marketing/campanas")        return new CampanasPage();
      if (appRoute === "/proyectos/dashboard")       return new ProyectosDashboardPage();
      if (appRoute === "/proyectos/proyectos")       return new ProyectosPage(false, !isSextante);
      if (appRoute === "/proyectos/mis-proyectos")   return new ProyectosPage(true, !isSextante);
      if (appRoute === "/proyectos/catalogo")        return new CatalogoPage();
      if (appRoute === "/proyectos/parametros")      return new ParametrosPage();
      // Rutas viejas de las páginas que Parámetros unificó -por si queda algún enlace suelto-.
      if (appRoute === "/proyectos/macroeconomico")  return new ParametrosPage();
      if (appRoute === "/proyectos/cosechas")        return new ParametrosPage();
      if (appRoute === "/proyectos/tecnicos")        return new TecnicosPage();
      if (appRoute === "/proyectos/nucleos")         return new NucleosPage();
      if (appRoute === "/proyectos/macro-proyectos") return new MacroProyectosPage();
      if (appRoute === "/proyectos/cultivos")        return new ProyectosCultivosPage();
      return null;
    },
  };
  //
  new Init<LoginPlataformaResponse>(data).init();
}
